import { useEffect, useState } from "react";
import { useAuth } from "@/orbit/context/AuthContext";
import { crmApi, formatApiError } from "@/orbit/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Trash2, ShieldCheck, UserPlus, X } from "lucide-react";

const TABS = [
  { id: "workspace", label: "Workspace" },
  { id: "team", label: "Team" },
  { id: "profile", label: "Profile" },
];

const ROLES = [
  { value: "client_admin", label: "Client Admin" },
  { value: "sales_manager", label: "Sales Manager" },
  { value: "sdr", label: "SDR / BDR" },
  { value: "sales_exec", label: "Sales Executive" },
  { value: "viewer", label: "Viewer" },
];

export default function Settings() {
  const { user, workspace, refreshMe } = useAuth();
  const [tab, setTab] = useState("workspace");

  return (
    <div className="max-w-5xl" data-testid="orbit-settings-page">
      <p className="text-[11px] uppercase tracking-[0.22em] text-[#1E40AF] font-semibold">Settings</p>
      <h1 className="mt-2 text-3xl font-semibold text-[#0A1F44]">Workspace settings</h1>

      <div className="mt-8 border-b border-slate-200 flex gap-6">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`orbit-settings-tab-${t.id}`}
            className={`pb-3 text-sm font-medium border-b-2 transition ${
              tab === t.id ? "border-[#0A1F44] text-[#0A1F44]" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "workspace" && <WorkspaceTab workspace={workspace} onUpdated={refreshMe} isAdmin={["client_admin", "super_admin"].includes(user?.role)} />}
        {tab === "team" && <TeamTab currentUser={user} />}
        {tab === "profile" && <ProfileTab user={user} />}
      </div>
    </div>
  );
}

function WorkspaceTab({ workspace, onUpdated, isAdmin }) {
  const [form, setForm] = useState({ name: "", timezone: "UTC", currency: "USD" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (workspace) setForm({ name: workspace.name || "", timezone: workspace.timezone || "UTC", currency: workspace.currency || "USD" });
  }, [workspace]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await crmApi.patch("/workspace", form);
      toast.success("Workspace updated");
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-8" data-testid="orbit-workspace-tab">
      <h3 className="text-lg font-semibold text-[#0A1F44]">General</h3>
      <p className="text-sm text-slate-500 mt-1">Basic workspace information visible to your team.</p>
      <form onSubmit={save} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl">
        <div className="md:col-span-2">
          <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Workspace name</label>
          <input type="text" required minLength={2} value={form.name} disabled={!isAdmin}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="orbit-workspace-name-input"
            className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40 focus:border-[#1E40AF]" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Timezone</label>
          <input type="text" value={form.timezone} disabled={!isAdmin}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            data-testid="orbit-workspace-tz-input"
            className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Currency</label>
          <input type="text" value={form.currency} disabled={!isAdmin}
            onChange={(e) => setForm({ ...form, currency: e.target.value })}
            data-testid="orbit-workspace-currency-input"
            className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40" />
        </div>
        {isAdmin && (
          <div className="md:col-span-2">
            <button type="submit" disabled={saving} data-testid="orbit-workspace-save"
              className="inline-flex items-center gap-2 bg-[#0A1F44] text-white px-5 py-2 rounded-md text-sm font-semibold hover:bg-[#1E40AF] transition disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save changes
            </button>
          </div>
        )}
        {!isAdmin && <p className="md:col-span-2 text-xs text-slate-400">Only Client Admins can edit workspace settings.</p>}
      </form>
    </div>
  );
}

function TeamTab({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const isAdmin = ["client_admin", "super_admin"].includes(currentUser?.role);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await crmApi.get("/workspace/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const remove = async (u) => {
    const isInvite = String(u.id).startsWith("invite:");
    const confirmMsg = isInvite
      ? `Revoke pending invitation to ${u.email}?`
      : `Remove ${u.first_name} ${u.last_name} from the workspace?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      if (isInvite) {
        // Look up the full token via /workspace/invitations then revoke
        const { data: invites } = await crmApi.get("/workspace/invitations");
        const match = invites.find((i) => i.email === u.email);
        if (!match) throw new Error("Invitation not found");
        await crmApi.delete(`/workspace/invitations/${match.token}`);
        toast.success("Invitation revoked");
      } else {
        await crmApi.delete(`/workspace/users/${u.id}`);
        toast.success("User removed");
      }
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Action failed");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="orbit-team-tab">
      <div className="p-6 flex items-center justify-between border-b border-slate-100">
        <div>
          <h3 className="text-lg font-semibold text-[#0A1F44]">Team members</h3>
          <p className="text-sm text-slate-500 mt-1">Manage who has access to this workspace.</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowInvite(true)} data-testid="orbit-invite-open"
            className="inline-flex items-center gap-2 bg-[#0A1F44] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#1E40AF] transition">
            <UserPlus className="w-4 h-4" /> Invite user
          </button>
        )}
      </div>
      {loading ? (
        <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading team…</div>
      ) : (
        <div className="divide-y divide-slate-100" data-testid="orbit-team-list">
          {users.map((u) => (
            <div key={u.id} className="p-6 flex items-center gap-4" data-testid={`orbit-team-row-${u.email}`}>
              <div className="w-10 h-10 bg-slate-100 text-slate-700 rounded-full flex items-center justify-center text-sm font-semibold">
                {u.first_name?.[0]?.toUpperCase()}{u.last_name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-[#0A1F44]">{u.first_name} {u.last_name}</span>
                  {u.status === "invited" && <span className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-700 px-2 py-0.5 rounded">Invited</span>}
                  {u.status === "disabled" && <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-0.5 rounded">Disabled</span>}
                  {u.id === currentUser?.id && <span className="text-[10px] uppercase tracking-wider bg-blue-50 text-[#1E40AF] px-2 py-0.5 rounded">You</span>}
                </div>
                <div className="text-sm text-slate-500 mt-0.5 flex items-center gap-2">
                  <Mail className="w-3 h-3" /> {u.email}
                  <span className="text-slate-300">·</span>
                  <span className="capitalize flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> {u.role.replace("_", " ")}</span>
                </div>
              </div>
              {isAdmin && u.id !== currentUser?.id && (
                <button onClick={() => remove(u)} data-testid={`orbit-team-remove-${u.email}`}
                  className="text-slate-400 hover:text-red-600 p-2 rounded-md hover:bg-red-50 transition">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {showInvite && <InviteModal onClose={() => { setShowInvite(false); fetchUsers(); }} />}
    </div>
  );
}

function InviteModal({ onClose }) {
  const [form, setForm] = useState({ email: "", role: "sdr", first_name: "", last_name: "" });
  const [loading, setLoading] = useState(false);
  const [invitedLink, setInvitedLink] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await crmApi.post("/workspace/users/invite", form);
      const link = `${window.location.origin}/orbit/accept-invite/${data.token}`;
      setInvitedLink(link);
      toast.success(`Invitation sent to ${form.email}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invite failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="orbit-invite-modal">
      <div className="bg-white w-full max-w-md rounded-md shadow-xl">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#0A1F44]">Invite teammate</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="orbit-invite-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        {invitedLink ? (
          <div className="p-6 space-y-3">
            <p className="text-sm text-slate-700">Invitation sent. Share this link if the email is not received (expires in 48 hours):</p>
            <div className="text-xs bg-slate-50 border border-slate-200 rounded p-3 break-all font-mono" data-testid="orbit-invite-link">{invitedLink}</div>
            <button onClick={onClose} className="w-full bg-[#0A1F44] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#1E40AF] transition" data-testid="orbit-invite-done">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4" data-testid="orbit-invite-form">
            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Email</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="orbit-invite-email"
                className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">First name</label>
                <input type="text" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  data-testid="orbit-invite-first-name"
                  className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Last name</label>
                <input type="text" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  data-testid="orbit-invite-last-name"
                  className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="orbit-invite-role"
                className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40">
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <button type="submit" disabled={loading} data-testid="orbit-invite-submit"
              className="w-full flex items-center justify-center gap-2 bg-[#0A1F44] text-white py-2.5 rounded-md text-sm font-semibold hover:bg-[#1E40AF] transition disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Send invitation
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function ProfileTab({ user }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-8 max-w-2xl" data-testid="orbit-profile-tab">
      <h3 className="text-lg font-semibold text-[#0A1F44]">Your profile</h3>
      <p className="text-sm text-slate-500 mt-1">This information is visible to your teammates.</p>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">First name</label>
          <div className="mt-1.5 text-sm text-[#0A1F44]">{user?.first_name}</div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Last name</label>
          <div className="mt-1.5 text-sm text-[#0A1F44]">{user?.last_name}</div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Email</label>
          <div className="mt-1.5 text-sm text-[#0A1F44]">{user?.email}</div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Role</label>
          <div className="mt-1.5 text-sm text-[#0A1F44] capitalize">{user?.role?.replace("_", " ")}</div>
        </div>
      </div>
      <p className="mt-8 text-xs text-slate-400">Profile editing (photo, name changes, password change) ships in Phase 1.</p>
    </div>
  );
}
